
// lodash components
import kebabCase from 'lodash/kebabCase'
import trim from 'lodash/trim'
import { url as _url } from '@js/utilities/url.js'

const applyPageStartRoute = window.TEMPLATE === 'apply' ? window.PAGE_SLUG : 'apply'

const stepStateDefaults = {
    completed: false,
    hideContinue: false,
    hideOnSummary: false,
    stringOnSummary: false,
    excludeFromProgress: false,
    excludeFromJourney: false,
    hideBack: false,
    index: null,
}

class JourneyCreator  {
    constructor(journey, allAvailableSteps, $t, store) {
        this.journey = journey
        this.allAvailableSteps = allAvailableSteps
        this.$t = $t
        this.store = store
        this.stepRoutes = []
        this.uniquePaths = []

        this.addHardcodedSteps()

        this.assignStepComponents(this.journey.data, `/${applyPageStartRoute}`)
    }

    addHardcodedSteps() {
        this.journey.data.unshift({
            type: 'step',
            key: 'summary'
        })

        this.journey.data.push({
            type: 'step',
            key: 'terms'
        })
    }

    assignStepComponents(childNode, url) {
        for (const node of childNode) {
            if (node.type === 'section' && node.children) {
                this.assignStepComponents(node.children, url + '/' + trim(kebabCase(node.name)), '/')
            } else if (node.type === 'step') {
                const component = this.allAvailableSteps[node.key]

                if (!component) {
                    console.error(node.key + ' component not found')
                } else {
                    const stepDefinition = component.stepDefinition(this.$t, this.store)

                    // router
                    if (Array.isArray(stepDefinition)) {
                        for (const innerStepDefinition of stepDefinition) {
                            if (innerStepDefinition.name == node.key) {
                                this.setupStepRoute(node, innerStepDefinition, component, url)
                            }
                        }
                    } else {
                        this.setupStepRoute(node, stepDefinition, component, url)
                    }
                }
            } else if (node.type === 'condition') {
                this.assignStepComponents(node.children_fail, url)
                this.assignStepComponents(node.children_pass, url)
            }
        }
    }

    setupStepRoute(node, stepDefinition, component, url) {
        const stepRoute = stepDefinition.route
        stepRoute['component'] = component

        if (stepRoute.relativeUrl) {
            stepRoute['path'] = _url(url + '/' + stepRoute.relativeUrl)
            delete stepRoute.relativeUrl
        } else {
            // absolute but still on the apply page
            stepRoute['path'] = _url(`/${applyPageStartRoute}/` + trim(stepRoute.absoluteUrl, '/'))
            delete stepRoute.absoluteUrl
        }

        // keep all routes unique
        if (this.uniquePaths.includes(stepRoute['path'])) {
            let i = 2
            while (this.uniquePaths.includes(stepRoute['path'] + '/' + i)) {
                i++
            }

            stepRoute['path'] = stepRoute['path'] + '/' + i
            stepRoute['name'] = stepRoute['name'] + '.' + i
        }

        this.stepRoutes.push(stepRoute)

        // state
        Object.assign(node, {...stepStateDefaults, ...stepDefinition})
        node.route = stepRoute
    }

    get() {
        return this.journey
    }

    getStepRoutes() {
        return this.stepRoutes
    }

    static getApplyPath() {
        return applyPageStartRoute
    }
}

/**
 * This entire class refreshes each time any of the journey data is changed (see getters). For example,
 * when a step is completed, this class is rerun and a new current step is calculated with all section
 * and step changes.
 */
class JourneyInfo {
    constructor(journeyStateData) {
        this.traversedSteps = []
        this.firstUncompletedStep = null
        this.sectionsCompleted = []
        this.currentSection = null
        this.validFieldConditionResultsFound = true
        this.stepIndex = 0
        this.sectionIndex = 0

        this.futureSteps = {
            potential: [],
            concrete: []
        }

        this.futureSections = {
            potential: [],
            concrete: []
        }

        // depending on the path travelled (according to logic epxressions)
        // objects to be passed byref
        this.stepsRemaining = {
            least: 0,
            most: 0
        }

        this.sectionsRemaining = {
            least: 0,
            most: 0
        }

        this.progress = {
            stepsCompleted: 0,
            stepsRemaining: {
                least: 0,
                most: 0
            }
        }

        // private
        this.tempSection = null

        // chart me path according to yer logic expressions! Arggg!
        this.chartPath(journeyStateData, this.stepsRemaining, this.sectionsRemaining, this.progress.stepsRemaining)
    }

    getNextStep() {
        let nextStep = this.futureSteps.concrete.length ? this.futureSteps.concrete[0] : null

        if (!nextStep) {
            nextStep = this.futureSteps.potential.length ? this.futureSteps.potential[0] : null
        }

        return nextStep
    }

    /**
     * DO all the condition-based fields have values so that we can check all conditions and
     * know what the path looks like right to the end of the form (even if this changes)
     */
    isPathTrajectoryDefined() {
        return !this.futureSteps.potential.length
    }

    getFirstUncompletedStep() {
        return this.firstUncompletedStep
    }

    getPreviousStep() {
        return this.getTraversedSteps().slice(-1)[0] || null
    }

    getStepBefore(step) {
        const pathSteps = this.getCurrentJourneyPath()
        const index = pathSteps.indexOf(step)

        if (index > 0) {
            return pathSteps[index - 1]
        }

        return null
    }

    getStepAfter(step) {
        const pathSteps = this.getCurrentJourneyPath()
        const index = pathSteps.indexOf(step)

        if (pathSteps.length - index > 0) {
            return pathSteps[index + 1]
        }

        return null
    }

    getFirstStep() {
        return this.getCurrentJourneyPath()[0] || null
    }

    isFirstStep() {
        return !this.traversedSteps.length
    }

    getTraversedSteps() {
        return this.traversedSteps
    }

    getTotalStepsCurrentPath() {
        return this.progress.stepsCompleted + this.progress.stepsRemaining.most
    }

    getCompletedStepsCurrentPath() {
        return this.progress.stepsCompleted
    }

    /**
     * Get field from the traversed steps (or current step)
     * @param {string} fieldName mapped or native
     * @returns string
     */
    getField(fieldName) {
        for (const step of this.getCurrentJourneyPath()) {
            if (!step.fields) {
                continue
            }

            for (const name in step.fields) {
                const field = step.fields[name]

                if (field.mappedKey == fieldName || field.legacyMappedKey == fieldName || name == fieldName) {
                    return field
                }
            }
        }

        return null
    }

    getSectionCountMost() {
        return this.getCurrentSectionNumber() + this.sectionsRemaining.most
    }

    getCurrentSectionNumber() {
        return this.sectionsCompleted.length + 1
    }

    getCurrentSection() {
        return this.currentSection
    }

    getCurrentJourneyPath(includeFutureConcreteSteps = true, includeFuturePotentialSteps = false) {
        return this.firstUncompletedStep ? [
            ...this.getTraversedSteps(),
            this.firstUncompletedStep,
            ...(includeFutureConcreteSteps ? this.getFutureConcreteSteps() : []),
            ...(includeFuturePotentialSteps ? this.getFuturePotentialSteps() : [])
        ] : this.getTraversedSteps()
    }

    /**
     * Return all future possible steps No conditional logic is followed so these are all steps no matter the path i.e. the multiverse of future steps 😉)
     * @returns array
     */
    getFuturePotentialSteps() {
        return this.futureSteps.potential
    }

    /**
     * Return all future that have passed valid conditional logic so that these are (currently) guaranteed to be the next steps
     * @returns array
     */
    getFutureConcreteSteps() {
        return this.futureSteps.concrete
    }

    getStepByName(stepName) {
        for (const step of this.getCurrentJourneyPath()) {
            if (step.name == stepName) {
                return step
            }
        }

        return null
    }

    firstSection() {
        return this.sectionsCompleted[0] ?? this.currentSection ?? this.futureSections.concrete[0] ?? this.futureSections.potential[0]
    }

    chartPath(childNode, stepsRemaining, sectionsRemaining, progressStepsRemaining) {
        for (const node of childNode) {
            if (node.type === 'section' && node.children) {
                if (!this.firstUncompletedStep) {
                    node.index = this.sectionIndex++
                    if (this.traversedSteps.length && this.tempSection) {
                        this.sectionsCompleted.push(this.tempSection)
                    }
                } else {
                    sectionsRemaining.most++
                    sectionsRemaining.least++

                    if (this.validFieldConditionResultsFound) {
                        node.index = this.sectionIndex++
                        this.futureSections.concrete.push(node)
                    } else {
                        node.index = null
                        this.futureSections.potential.push(node)
                    }
                }

                this.tempSection = node

                this.chartPath(node.children, stepsRemaining, sectionsRemaining, progressStepsRemaining)
            } else if (node.type === 'step' && !node.excludeFromJourney) {
                node.section = this.tempSection

                if (node.fields) {
                    for (const name in node.fields) {
                        const field = node.fields[name]

                        if (!field?.step) {
                            field.step = node
                        }
                    }
                }

                if (!this.firstUncompletedStep) {
                    node.index = this.stepIndex++

                    if (node.completed) {
                        this.traversedSteps.push(node)

                        if (!node.excludeFromProgress) {
                            this.progress.stepsCompleted++
                        }
                    } else {
                        this.firstUncompletedStep = node
                        this.currentSection = this.tempSection
                    }
                } else {
                    stepsRemaining.most++
                    stepsRemaining.least++

                    if (this.validFieldConditionResultsFound) {
                        node.index = this.stepIndex++
                        this.futureSteps.concrete.push(node)
                    } else {
                        // possible steps can't have an index as it could be any one
                        this.futureSteps.potential.push(node)
                    }

                    if (!node.excludeFromProgress) {
                        progressStepsRemaining.most++
                        progressStepsRemaining.least++
                    }
                }
            } else if (node.type === 'condition') {
                const expressionResults = []
                for (const expression of node.expressions) {
                    const expResult = this.testExpression(expression)

                    if (expResult === null) {
                        this.validFieldConditionResultsFound = false
                    }

                    expressionResults.push(expResult)
                }

                // if current step is not set, we need to power though even if there are null fields to test
                // we also want to test into the future (past firstUncompletedStep) for fields that are not null
                if (!this.firstUncompletedStep || this.validFieldConditionResultsFound) {
                    if (
                        (node.match == 'all' && expressionResults.every((result) => { return result })) ||
                        (node.match == 'any' && expressionResults.some((result) => { return result }))
                    ) {
                        this.chartPath(node.children_pass, stepsRemaining, sectionsRemaining, progressStepsRemaining)
                    } else {
                        this.chartPath(node.children_fail, stepsRemaining, sectionsRemaining, progressStepsRemaining)
                    }
                } else {
                    const pass = {
                        stepsRemaining: {
                            least: 0,
                            most: 0
                        },
                        sectionsRemaining: {
                            least: 0,
                            most: 0
                        },
                        progressStepsRemaining: {
                            least: 0,
                            most: 0
                        },
                    }

                    const fail = {
                        stepsRemaining: {
                            least: 0,
                            most: 0
                        },
                        sectionsRemaining: {
                            least: 0,
                            most: 0
                        },
                        progressStepsRemaining: {
                            least: 0,
                            most: 0
                        },
                    }

                    this.chartPath(node.children_pass, pass.stepsRemaining, pass.sectionsRemaining, pass.progressStepsRemaining)
                    this.chartPath(node.children_fail, fail.stepsRemaining, fail.sectionsRemaining, fail.progressStepsRemaining)

                    stepsRemaining.most += Math.max(pass.stepsRemaining.most, fail.stepsRemaining.most)
                    stepsRemaining.least += Math.min(pass.stepsRemaining.most, fail.stepsRemaining.most)

                    sectionsRemaining.most += Math.max(pass.sectionsRemaining.most, fail.sectionsRemaining.most)
                    sectionsRemaining.least += Math.min(pass.sectionsRemaining.most, fail.sectionsRemaining.most)

                    progressStepsRemaining.most += Math.max(pass.progressStepsRemaining.most, fail.progressStepsRemaining.most)
                    progressStepsRemaining.least += Math.min(pass.progressStepsRemaining.most, fail.progressStepsRemaining.most)
                }
            }
        }
    }

    testExpression(expression) {
        const fieldValue = this.getField(expression.field)?.value

        // if it is actual null, then it's not set yet so we shouldn't test
        if (fieldValue === null) {
            return null
        }

        let expResult
        // instead of using dangerous eval(), we'll explicitly check the logic
        switch (expression.logic) {
            case '==':
                expResult = fieldValue == expression.value
                break

            case '!=':
                expResult = fieldValue != expression.value
                break

            case '>':
                expResult = fieldValue > expression.value
                break

            case '<':
                expResult = fieldValue < expression.value
                break

            case '>=':
                expResult = fieldValue >= expression.value
                break

            case '<=':
                expResult = fieldValue <= expression.value
                break

            default:
                expResult = false
                break;
        }

        return expResult
    }
}

export { JourneyCreator, JourneyInfo }
